import React, { useEffect } from 'react';
import { channelMark, channelName, dayTime, postText, postTime, ReachPost, time } from './reach';

export const TONE: Record<string, { dot: string; border: string; label: string }> = {
  posted: { dot: 'var(--ink-green)', border: 'var(--ink-line)', label: 'Posted' },
  publishing: { dot: 'var(--ink-green)', border: 'var(--ink-line)', label: 'Posting' },
  scheduled: { dot: 'var(--ink-accent)', border: 'var(--ink-line)', label: 'Scheduled' },
  waiting: { dot: 'var(--ink-amber)', border: 'var(--ink-amber)', label: 'Waiting on you' },
  // A post whose time passed without going out (Prototype v7: "Didn't post").
  missed: { dot: 'var(--ink-red)', border: 'var(--ink-red)', label: 'Didn’t post' },
};

/**
 * One post, opened from the Calendar's week or from a channel's "Last post
 * didn't go out · Fix" (Prototype v7): what it says, when, and — for one that
 * didn't go out — why, with Try again / Skip. A waiting post can be approved or
 * skipped here too.
 */
export function PostPanel({
  post,
  busy,
  onClose,
  onAct,
}: {
  post: ReachPost;
  busy: boolean;
  onClose: () => void;
  onAct: (kind: 'approve' | 'skip' | 'retry') => void;
}) {
  const tone = TONE[post.reachStatus] || TONE.scheduled;
  const t = postTime(post);
  const channel = channelName(post.platform);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const missed = post.reachStatus === 'missed';
  const waiting = post.reachStatus === 'waiting';
  return (
    <>
      <div className="rc-panel-back" onClick={onClose} />
      <aside className="rc-panel" role="dialog" aria-label={`${channel} post`}>
        <div className="rc-panel-head">
          <span className="rc-mark">{channelMark(post.platform)}</span>
          <div className="rc-panel-title">
            <strong>{channel}</strong>
            <span className="rc-panel-status">
              <span className="ink-dot" style={{ background: tone.dot }} />
              <span style={{ color: missed ? 'var(--ink-red)' : undefined }}>{tone.label}</span>
              {t ? <span className="ink-muted"> · {dayTime(t)}</span> : null}
            </span>
          </div>
          <button type="button" className="rc-panel-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="rc-panel-body">
          <div className="rc-panel-post">{postText(post) || 'Untitled post'}</div>
          {missed ? (
            <p className="rc-panel-why">
              {channel} didn’t take this post{t ? ` at ${time(t)}` : ''}
              {post.failure ? `: ${post.failure}` : ''}. Nothing was posted.
            </p>
          ) : null}
        </div>
        {missed || waiting ? (
          <div className="rc-panel-foot">
            <button type="button" className="ink-btn" disabled={busy} onClick={() => onAct('skip')}>
              Skip
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={busy}
              onClick={() => onAct(missed ? 'retry' : 'approve')}
            >
              {missed ? 'Try again' : 'Approve'}
            </button>
          </div>
        ) : null}
      </aside>
    </>
  );
}
